import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Col,
  Row,
  UiBox,
  UiFormControl,
  UiFormErrorMessage,
  UiImage,
  UiQuestionnaireLayout,
  UiText,
  UiTextarea,
} from '~/components/elements';
import {ScrollToBottomButton} from '~/components/shared/ScrollToBottomButton';
import {PERIOD_CONTROL_MEDICATION_KEYS, NO_OPTION, YES_OPTION} from '~/constants/medical-questionnaires';
import {useInput} from '~/hooks';
import {setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {RootState} from '~/redux/root-reducer';
import {AppDispatch} from '~/redux/root-store';
import {spacing} from '~/utils/theme/spacing';

const Questionnaire7Screen = () => {
  const rules = {
    medicationsTaking: () => {
      if (inputs?.selectedOption === YES_OPTION) {
        return {
          presence: {allowEmpty: false, message: `^This field can't be blank`},
          length: {
            minimum: 5,
            maximum: 300,
            tooShort: '^This field is too short (minimum is %{count} characters)',
            tooLong: '^This field is too long (maximum is %{count} characters)',
            tokenizer: function (value: any) {
              return trimSpaces(value);
            },
          },
        };
      }
      return undefined;
    },
  };

  const dispatch: AppDispatch = useDispatch();
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = questionnaires?.find(
    i => i.key === PERIOD_CONTROL_MEDICATION_KEYS.OTHER_MEDICATIONS_OR_SUPPLEMENTS,
  );
  const currentAnswer = useSelector(
    (state: RootState) =>
      state.questionnaires?.collectData[PERIOD_CONTROL_MEDICATION_KEYS.OTHER_MEDICATIONS_OR_SUPPLEMENTS]?.data,
  );
  const {inputs, setInputs, validation, trimSpaces} = useInput(
    {
      selectedOption: currentAnswer?.selectedOption || '',
      medicationsTaking: currentAnswer?.medicationsTaking || '',
    },
    {rules},
  );

  const onClickSubmit = () => {
    if (inputs.selectedOption === YES_OPTION) {
      const errors = validation.getErrors();
      if (errors) {
        return;
      }
    }
    dispatch(
      setQuestionnaireAnswer({
        key: PERIOD_CONTROL_MEDICATION_KEYS.OTHER_MEDICATIONS_OR_SUPPLEMENTS,
        index: questionnaire?.index || '',
        data: inputs,
      }),
    );
    dispatch(setNextQuestionnaire({value: PERIOD_CONTROL_MEDICATION_KEYS.OTHER_MEDICAL_HISTORY}));
  };

  const onClickOption = (value: string) => {
    if (value === NO_OPTION) {
      setInputs({selectedOption: value, medicationsTaking: ''});
    }
    setInputs({selectedOption: value});
  };

  const renderOptions = () => {
    if (inputs.selectedOption === YES_OPTION) {
      const selectedOptionData = questionnaire?.options?.find(i => i.value === inputs.selectedOption);
      return (
        <UiBox w="100%" my={spacing.small}>
          {selectedOptionData?.relations?.map(i => {
            if (i?.elementType?.name === 'inputText') {
              return (
                <UiBox
                  w="100%"
                  mt={spacing.small}
                  key={i?.key || i?.elementType?.name}
                  className="animate__animated animate__fadeInLeft"
                >
                  <UiText fontSize="md" fontWeight="600" my={spacing.small}>
                    {i?.questions?.default}
                  </UiText>
                  <UiFormControl id="medicationsTaking" isRequired isInvalid={!!validation?.errors?.medicationsTaking}>
                    <UiTextarea
                      mt={spacing.small}
                      bg="white"
                      borderRadius={spacing.tiny}
                      placeholder={i?.elementType?.placeholder}
                      value={inputs.medicationsTaking}
                      onChange={({target}) => setInputs({medicationsTaking: target.value})}
                      minH="100px"
                    />
                    {validation?.errors?.medicationsTaking && (
                      <UiFormErrorMessage>{validation?.errors?.medicationsTaking[0]}</UiFormErrorMessage>
                    )}
                  </UiFormControl>
                </UiBox>
              );
            }
          })}
        </UiBox>
      );
    }
    return null;
  };

  const valid =
    inputs.selectedOption === YES_OPTION ? Object.values(inputs).every(el => !!el) : !!inputs.selectedOption;

  return (
    <UiQuestionnaireLayout
      handleOnNext={onClickSubmit}
      checkoutStep={questionnaire?.checkoutStep}
      actions={questionnaire?.actions}
      disableNext={!valid}
    >
      <Col align="center" textAlign="center">
        <UiText fontSize="xl" fontWeight="bold" my={spacing.medium}>
          {questionnaire?.questions?.default}
        </UiText>
        <Row w="100%">
          {questionnaire?.options?.map(value => (
            <ScrollToBottomButton
              key={`${value.label}-${value.value}`}
              title={value.label}
              variant={inputs.selectedOption === value.value ? 'solid' : 'toggle'}
              onClick={() => onClickOption(value.value as string)}
              height="35px"
              w="100%"
              mb={5}
              mx={2}
            />
          ))}
        </Row>
        {renderOptions()}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default Questionnaire7Screen;
